/**
 * QuickCapture — floating action button for rapid note entry
 * Collapsed: small circular + button fixed bottom-right
 * Expanded: compact glassmorphic card with textarea above the button
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NoteCategory } from '@maco144/shared';
import { CANVAS_THEME } from './canvas-theme';
import { useNotes } from './use-notes';

export interface QuickCaptureProps {
  userId?: string;
}

export function QuickCapture({ userId }: QuickCaptureProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [text, setText] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { createNote } = useNotes(userId);

  // Autofocus textarea when expanded
  useEffect(() => {
    if (isExpanded && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isExpanded]);

  const handleSave = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed) {
      setIsExpanded(false);
      setText('');
      return;
    }

    // Derive title: first line, capped at 40 chars
    const firstLine = trimmed.split('\n')[0];
    const title = firstLine.length > 40 ? firstLine.slice(0, 40) : firstLine;

    const now = Date.now();
    await createNote({
      noteType: 'main',
      title,
      content: trimmed,
      category: NoteCategory.GENERAL,
      tags: [],
      createdAt: now,
      updatedAt: now,
    });

    setText('');
    setIsExpanded(false);
  }, [text, createNote]);

  const handleCancel = useCallback(() => {
    setText('');
    setIsExpanded(false);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        handleCancel();
      }
    },
    [handleSave, handleCancel]
  );

  const buttonSize = 48;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 28,
        right: 28,
        zIndex: CANVAS_THEME.zIndex.toolbar + 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 8,
      }}
    >
      {/* Expanded card — rendered above the button */}
      {isExpanded && (
        <div
          style={{
            background: CANVAS_THEME.panelBackground,
            backdropFilter: CANVAS_THEME.panelBackdrop,
            WebkitBackdropFilter: CANVAS_THEME.panelBackdrop,
            border: CANVAS_THEME.panelBorder,
            borderRadius: 16,
            boxShadow: CANVAS_THEME.shadowHeavy,
            padding: '12px',
            width: 280,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <textarea
            ref={textareaRef}
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Capture a thought..."
            style={{
              width: '100%',
              boxSizing: 'border-box',
              background: 'rgba(255, 255, 255, 0.35)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: 10,
              padding: '8px 10px',
              color: CANVAS_THEME.textPrimary,
              fontSize: 13,
              fontFamily: 'inherit',
              lineHeight: 1.5,
              resize: 'none',
              outline: 'none',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <button
              onClick={handleCancel}
              style={{
                background: 'rgba(255, 255, 255, 0.25)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                borderRadius: 8,
                color: CANVAS_THEME.textSecondary,
                fontSize: 12,
                padding: '4px 10px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                lineHeight: 1.4,
                transition: CANVAS_THEME.transitionShift,
              }}
            >
              ✕
            </button>
            <button
              onClick={handleSave}
              style={{
                background: CANVAS_THEME.accentGradient,
                border: 'none',
                borderRadius: 8,
                color: '#fff',
                fontSize: 12,
                fontWeight: 600,
                padding: '4px 14px',
                cursor: 'pointer',
                fontFamily: 'inherit',
                lineHeight: 1.4,
                transition: CANVAS_THEME.transitionShift,
                boxShadow: CANVAS_THEME.shadowLight,
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Floating action button */}
      <button
        onClick={() => setIsExpanded((v) => !v)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        aria-label="Quick capture note"
        style={{
          width: buttonSize,
          height: buttonSize,
          borderRadius: '50%',
          background: isExpanded ? CANVAS_THEME.accentGradient : 'rgba(255, 255, 255, 0.55)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: `2px solid ${CANVAS_THEME.accentColor}`,
          boxShadow: isHovered ? CANVAS_THEME.shadowHeavy : CANVAS_THEME.shadowMedium,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 24,
          fontWeight: 300,
          lineHeight: 1,
          color: isExpanded ? '#fff' : CANVAS_THEME.accentColor,
          transition: `transform ${CANVAS_THEME.transitionShift}, box-shadow ${CANVAS_THEME.transitionShift}, background ${CANVAS_THEME.transitionShift}`,
          transform: isHovered ? 'scale(1.08)' : 'scale(1)',
          padding: 0,
          userSelect: 'none',
        }}
      >
        {isExpanded ? '×' : '+'}
      </button>
    </div>
  );
}
